"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Shield, Clock, Award, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";
export default function WhyChooseUs() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const valuesRef = useRef<(HTMLDivElement | null)[]>([]);
  const coreValues = [{
    icon: Shield,
    title: "Privacy & Discretion",
    description: "Your confidentiality is our priority. Every journey is handled with the utmost professionalism and privacy."
  }, {
    icon: Clock,
    title: "Punctuality Guaranteed",
    description: "We understand the value of your time. Our chauffeurs arrive early and ensure you reach your destination on schedule."
  }, {
    icon: Award,
    title: "Premium Fleet",
    description: "Immaculate luxury sedans and SUVs, meticulously maintained to provide the highest level of comfort and style."
  }, {
    icon: Star,
    title: "Professional Excellence",
    description: "Our experienced chauffeurs are trained to deliver exceptional service, combining courtesy with expert local knowledge."
  }];
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const section = sectionRef.current;
      if (!section) return;
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      valuesRef.current.forEach((valueEl, index) => {
        if (!valueEl) return;
        const valueTop = valueEl.offsetTop + sectionTop;
        const valueHeight = valueEl.offsetHeight;
        if (scrollPosition >= valueTop && scrollPosition <= valueTop + valueHeight) {
          setActiveIndex(index);
        }
      });
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sectionRef]);
  const parallaxOffset = scrollY * 0.3;
  return <section id="why-choose-us" ref={sectionRef} className="dark bg-background text-foreground relative overflow-hidden section-padding">
      <div className="absolute inset-0 bg-gradient-to-b from-dark-section via-dark-section to-background/95 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {}
          <div className="space-y-8">
            <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4 text-white">
                Why Choose Us
              </h2>
              <p className="text-lg text-white/70 max-w-xl">
                Experience the difference that true luxury transportation brings
                to your journey through Los Angeles.
              </p>
              <img src="https://humane-alien.10web.cloud/wp-content/uploads/2026/02/Screenshot-2026-02-11-001721.jpg" alt="Cars at airports" className="mt-6 rounded-lg shadow-medium object-cover max-w-xl !w-[800px] !h-[300px]" />
            </div>

            <div className="space-y-6" data-index={0}>
              {coreValues.map((value, i) => {
              const Icon = value.icon;
              const isActive = activeIndex === i;
              return <div key={i} data-index={i} ref={el => {
                valuesRef.current[i] = el;
              }} className={`group relative p-6 rounded-lg border border-white/10 backdrop-blur-sm transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isActive ? "bg-white/10 shadow-[0_0_30px_rgba(255,255,255,0.15)] border-white/30" : "bg-white/5 hover:bg-white/8"}`}>
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-md transition-all duration-[var(--duration-medium)] ${isActive ? "bg-secondary/20 text-secondary shadow-[0_0_20px_rgba(255,193,7,0.3)]" : "bg-white/10 text-white/60"}`}>
                        <Icon className="size-6" />
                      </div>

                      <div className="flex-1">
                        <h3 className={`text-xl font-semibold mb-2 transition-colors duration-[var(--duration-medium)] ${isActive ? "text-white" : "text-white/80"}`}>
                          {value.title}
                        </h3>
                        <p className={`text-sm leading-relaxed transition-colors duration-[var(--duration-medium)] ${isActive ? "text-white/90" : "text-white/60"}`}>
                          {value.description}
                        </p>
                      </div>
                    </div>

                    {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-secondary via-secondary/80 to-transparent rounded-l-lg" />}
                  </div>;
            })}
            </div>
          </div>

          {}
          <div className="relative lg:sticky lg:top-24">
            <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
              <div className="relative rounded-lg overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center bg-[url(/img/luxury_chauffeur_car_detail.png)] transition-transform duration-1000 ease-out" style={{
                transform: `translateY(${parallaxOffset}px)`
              }} />

                <div className="relative aspect-[4/5] bg-gradient-to-br from-primary-dark/60 via-transparent to-dark-section/40 backdrop-blur-[2px]">
                  <div className="absolute inset-0 flex items-end p-8 lg:p-10">
                    <div className="glass-effect p-6 lg:p-8 space-y-4">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="h-1 w-12 bg-secondary rounded-full" />
                        <span className="text-secondary text-sm font-semibold uppercase tracking-wider">
                          Our Promise
                        </span>
                      </div>

                      <h3 className="text-2xl lg:text-3xl font-display font-bold text-white leading-tight">
                        Elevating Every Mile of Your Journey
                      </h3>

                      <p className="text-white/80 leading-relaxed">At Phoenix Enterprises Limousine Service , we don't just provide transportation—we craft experiences. From the moment you step into one of our pristine vehicles until you reach your destination, every detail is orchestrated to exceed your expectations. Our commitment to excellence, combined with years of experience serving Los Angeles, ensures that your journey is as memorable as your destination.</p>

                      <div className="pt-4 border-t border-white/20">
                        <p className="text-white/70 text-sm italic">
                          "Trusted by executives, celebrities, and discerning
                          travelers across Southern California."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}